import {
  IntegrationConnectionResource,
  IntegrationConnectionType,
  IntegrationConnectionTypes,
} from "@arrowsup/platform-dtos";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  ReferenceInput,
  SelectInput,
  useCreate,
  useRefresh,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { apiErrorMessage } from "../../logic/api/api-error-message";
import { integrationConnectionKeyHelp } from "./integration-connection";

const statusLabels: Record<string, string> = {
  access_lost: "access lost",
  disabled: "disabled",
};

const optionText = (c: IntegrationConnectionResource) =>
  c.status && c.status !== "active"
    ? `${c.name} (${statusLabels[c.status] ?? c.status})`
    : c.name;

/** Adds a `type` connection in a dialog, for picking it straight away. */
const AddConnectionButton: React.FC<{
  type: IntegrationConnectionType;
  onAdded: (connection: IntegrationConnectionResource) => void;
}> = ({ type, onAdded }) => {
  const label = IntegrationConnectionTypes[type].label;
  const [create, { isPending }] = useCreate<IntegrationConnectionResource>();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(label);
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string>();

  const close = () => {
    setOpen(false);
    setApiKey("");
    setError(undefined);
  };

  const add = () =>
    create(
      "integration-connection",
      { data: { type, name, apiKey } },
      {
        onSuccess: (connection) => {
          refresh();
          onAdded(connection);
          close();
        },
        onError: (e) => setError(apiErrorMessage(e)),
      }
    );

  return (
    <>
      <Button onClick={() => setOpen(true)} sx={{ mt: 1, flexShrink: 0 }}>
        Add connection
      </Button>
      <Dialog open={open} onClose={close} fullWidth>
        <DialogTitle>New {label} connection</DialogTitle>
        <DialogContent>
          <MuiTextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="dense"
            required
            fullWidth
          />
          <MuiTextField
            label="API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            helperText={`${integrationConnectionKeyHelp[type]} Arrows Up checks it works before saving.`}
            margin="dense"
            required
            fullWidth
          />
          {error && <Typography color="error">{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void add()}
            disabled={isPending || !name.trim() || !apiKey.trim()}
          >
            {isPending ? "Checking…" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/** Picks the `type` connection an input stream reads through. */
export const IntegrationConnectionInput: React.FC<{
  type: IntegrationConnectionType;
}> = ({ type }) => {
  const label = IntegrationConnectionTypes[type].label;
  const { setValue } = useFormContext();

  return (
    <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
      <ReferenceInput
        source="integrationConnection.id"
        reference="integration-connection"
        filter={{ type }}
        sort={{ field: "name", order: "ASC" }}
      >
        <SelectInput
          label={`${label} Connection`}
          optionText={optionText}
          helperText={`Holds the ${label} API key. Manage connections under Connections.`}
          required
          fullWidth
        />
      </ReferenceInput>
      <AddConnectionButton
        type={type}
        onAdded={(c) =>
          setValue("integrationConnection.id", c.id, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
    </Box>
  );
};
